import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { formatCurrency } from "../utils/formatters";
import {
  canConvertToJob,
  canSendToCustomer,
  canApprove,
  canReject,
  getStatusColor,
} from "../utils/estimateStatus";
import {
  FileText,
  DotsThree,
  CheckCircle,
  PaperPlaneTilt,
  Briefcase,
  Receipt,
  Eye,
  X,
} from "phosphor-react";

// Import modular estimate components
import {
  EstimateCreateModal,
  EstimateDetailModal,
  EstimateEditModal,
  EstimateDeleteDialog,
} from "../components/estimates";
import PayDepositModal from "../components/estimates/PayDepositModal";
import BottomQuickActions from "../components/BottomQuickActions";
import SharePromptCard from "../components/SharePromptCard";
import { shouldShowSharePrompt } from "../utils/sharePrompt";
import { useLiveFilter, getEstimateSearchText } from "../hooks/useLiveFilter";
import { useSearchFilter } from "../layouts/AppLayout";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Custom status order for sorting
const STATUS_ORDER = {
  approved: 0,
  draft: 1,
  sent: 2,
  rejected: 3,
  converted: 4,
  declined: 5,
};

export default function Estimates() {
  // URL search params for deep linking (e.g., from notifications)
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data state
  const [estimates, setEstimates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taxRatePercent, setTaxRatePercent] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [depositDefaults, setDepositDefaults] = useState({
    default_deposit_enabled: false,
    default_deposit_type: "PERCENT",
    default_deposit_value: 30,
  });

  // Use shared filter from layout header
  const { estimatesFilter: filterQuery } = useSearchFilter();
  
  // Navigation for auto-open modal
  const location = useLocation();
  const navigate = useNavigate();

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  
  // Share prompt state
  const [showSharePrompt, setShowSharePrompt] = useState(false);
  
  // Deposit payment state (for online payment after approval)
  const [showPayDepositModal, setShowPayDepositModal] = useState(false);
  const [pendingDepositInvoice, setPendingDepositInvoice] = useState(null);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    customer_id: "",
    title: "",
    items: [{ description: "", quantity: "", price: "", is_taxable: false }],
    notes: "",
    // Deposit fields will be prefilled from defaults after fetch
    deposit_enabled: false,
    deposit_type: "PERCENT",
    deposit_value: 30,
  });
  const [editFormData, setEditFormData] = useState(null);
  const [lineItemErrors, setLineItemErrors] = useState([]); // Validation errors

  // Memoized search text extractor for estimates
  const estimateSearchText = useCallback(getEstimateSearchText, []);

  // Sync deposit defaults to form when defaults are loaded
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      deposit_enabled: depositDefaults.default_deposit_enabled,
      deposit_type: depositDefaults.default_deposit_type,
      deposit_value: depositDefaults.default_deposit_value,
    }));
  }, [depositDefaults]);

  // Apply status filter first
  const statusFilteredEstimates = useMemo(() => {
    if (statusFilter === "all") return estimates;
    return estimates.filter(e => e.status === statusFilter);
  }, [estimates, statusFilter]);

  // Live filter by text query
  const filteredEstimates = useLiveFilter(statusFilteredEstimates, filterQuery, estimateSearchText);

  // Sort filtered estimates: status order + oldest to newest by date
  const displayedEstimates = useMemo(() => {
    return [...filteredEstimates].sort((a, b) => {
      const statusDiff = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
      if (statusDiff !== 0) return statusDiff;
      
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateA - dateB;
    });
  }, [filteredEstimates]);

  // ========================
  // DATA FETCHING
  // ========================

  useEffect(() => {
    fetchEstimates();
    fetchCustomers();
    fetchTaxSettings();
    fetchDepositSettings();
    fetchPaymentStatus();
  }, []);
  
  // Handle ?new=1 query param to auto-open create modal
  useEffect(() => {
    const shouldOpenNew = searchParams.get("new") === "1";
    if (shouldOpenNew && !isCreateModalOpen) {
      setIsCreateModalOpen(true);
      // Remove the param to prevent re-opening on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [searchParams, isCreateModalOpen, navigate, location.pathname]);
  
  // Handle deep link from notifications (?open=estimate_id)
  useEffect(() => {
    const openEstimateId = searchParams.get("open");
    if (openEstimateId && estimates.length > 0 && !isDetailModalOpen) {
      const estimateToOpen = estimates.find(e => e.estimate_id === openEstimateId);
      if (estimateToOpen) {
        setSelectedEstimate(estimateToOpen);
        setIsDetailModalOpen(true);
        // Clear the search param to prevent re-opening on refresh
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, estimates, isDetailModalOpen, setSearchParams]);

  const fetchEstimates = async () => {
    try {
      const response = await fetch(`${API}/estimates`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setEstimates(data.estimates);
      }
    } catch (error) {
      console.error("Error fetching estimates:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      // Fetch initial customers for the picker (server-side search will be handled by the component)
      const response = await fetch(`${API}/customers?limit=50`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchTaxSettings = async () => {
    try {
      const response = await fetch(`${API}/estimates/tax-settings`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setTaxRatePercent(data.tax_rate_percent || 0);
      }
    } catch (error) {
      console.error("Error fetching tax settings:", error);
    }
  };

  const fetchDepositSettings = async () => {
    try {
      const response = await fetch(`${API}/tenants/deposit-settings`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setDepositDefaults({
          default_deposit_enabled: data.default_deposit_enabled ?? false,
          default_deposit_type: data.default_deposit_type ?? "PERCENT",
          default_deposit_value: data.default_deposit_value ?? 30,
        });
      }
    } catch (error) {
      console.error("Error fetching deposit settings:", error);
    }
  };

  const fetchPaymentStatus = async () => {
    try {
      // Check if tenant has payment processing enabled
      const response = await fetch(`${API}/billing/tokenization-bootstrap`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        // Payments are enabled if we get a valid Finix config
        setPaymentsEnabled(data.ok && data.provider === "finix");
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setPaymentsEnabled(false);
    }
  };

  // ========================
  // CREATE ESTIMATE
  // ========================

  const resetForm = () => {
    setFormData({
      customer_id: "",
      title: "",
      items: [{ description: "", quantity: "", price: "", is_taxable: false }],
      notes: "",
      // Prefill deposit config from tenant defaults
      deposit_enabled: depositDefaults.default_deposit_enabled,
      deposit_type: depositDefaults.default_deposit_type,
      deposit_value: depositDefaults.default_deposit_value,
    });
    setLineItemErrors([]);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    
    // Import validation and normalization functions
    const { validateLineItems, normalizeLineItems } = await import("../components/LineItemsEditor");
    const { normalizeApiError } = await import("../utils/apiErrorNormalizer");
    
    // Normalize line items first (auto-fill qty=1 if price set but qty empty)
    const normalizedItems = normalizeLineItems(formData.items);
    const normalizedFormData = { ...formData, items: normalizedItems };
    
    // Client-side validation
    const errors = validateLineItems(normalizedItems);
    if (errors.length > 0) {
      setLineItemErrors(errors);
      toast.error("Please fix the errors in line items before saving.");
      return;
    }
    setLineItemErrors([]);
    
    try {
      const response = await fetch(`${API}/estimates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(normalizedFormData),
      });

      if (response.ok) {
        toast.success("Estimate created");
        setIsCreateModalOpen(false);
        resetForm();
        fetchEstimates();
      } else {
        const data = await response.json();
        const friendlyError = normalizeApiError({ response: { status: response.status, data } });
        toast.error(friendlyError);
      }
    } catch (error) {
      const { normalizeApiError } = await import("../utils/apiErrorNormalizer");
      toast.error(normalizeApiError(error));
    }
  };

  // Line item handlers for create form
  const addLineItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: "", price: "", is_taxable: false }],
    });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.items];
    if (field === "quantity") {
      // Allow empty, store as string for input control
      newItems[index][field] = value;
    } else if (field === "is_taxable") {
      newItems[index][field] = value;
    } else {
      newItems[index][field] = value;
    }
    setFormData({ ...formData, items: newItems });
  };

  const formatPrice = (index) => {
    const newItems = [...formData.items];
    const val = parseFloat(newItems[index].price);
    if (!isNaN(val) && val > 0) {
      newItems[index].price = val.toFixed(2);
      // AUTO-FILL: If price is set and qty is empty, set qty to 1
      if (newItems[index].quantity === "" || newItems[index].quantity === null || newItems[index].quantity === undefined) {
        newItems[index].quantity = "1";
      }
    } else if (newItems[index].price === "" || newItems[index].price === "0") {
      newItems[index].price = "";
    }
    setFormData({ ...formData, items: newItems });
  };

  const handleCatalogSelect = (index, catalogItem) => {
    const newItems = [...formData.items];
    const currentQuantity = newItems[index].quantity;
    // Preserve existing quantity if user has set one, otherwise keep empty
    newItems[index] = {
      ...newItems[index],
      description: catalogItem.description,
      price: catalogItem.price.toFixed(2),
      is_taxable: catalogItem.is_taxable,
      quantity: currentQuantity !== "" ? currentQuantity : "",
      catalog_item_id: catalogItem.catalog_item_id,
    };
    setFormData({ ...formData, items: newItems });
  };

  const removeLineItem = (index) => {
    if (formData.items.length > 1) {
      setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
    }
  };

  // ========================
  // VIEW ESTIMATE
  // ========================

  const viewEstimate = (estimate) => {
    setSelectedEstimate(estimate);
    setIsDetailModalOpen(true);
  };

  // ========================
  // ESTIMATE ACTIONS
  // ========================

  const approveEstimate = async (estimateId) => {
    try {
      const response = await fetch(`${API}/estimates/${estimateId}/approve`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        const approvedEstimate = await response.json();
        toast.success("Estimate approved");
        
        // Check if deposit payment is required and payments are enabled
        if (approvedEstimate.deposit_required && approvedEstimate.deposit_invoice_id && paymentsEnabled) {
          // Fetch the deposit invoice details for payment
          try {
            const invoiceResponse = await fetch(`${API}/invoices/${approvedEstimate.deposit_invoice_id}`, {
              credentials: "include",
            });
            if (invoiceResponse.ok) {
              const depositInvoice = await invoiceResponse.json();
              setPendingDepositInvoice(depositInvoice);
              setSelectedEstimate(approvedEstimate);
              setShowPayDepositModal(true);
              setIsDetailModalOpen(false);
              return; // Don't close everything yet - wait for payment modal
            }
          } catch (err) {
            console.error("Error fetching deposit invoice:", err);
            // Continue with normal flow if invoice fetch fails
          }
        }
        
        // Normal flow - no deposit or payments not enabled
        fetchEstimates();
        setIsDetailModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to approve estimate");
      }
    } catch (error) {
      toast.error("Failed to approve estimate");
    }
  };

  const rejectEstimate = async (estimateId, reason = "") => {
    try {
      const response = await fetch(`${API}/estimates/${estimateId}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ rejection_reason: reason }),
      });
      if (response.ok) {
        toast.success("Estimate marked as rejected");
        fetchEstimates();
        setIsDetailModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reject estimate");
      }
    } catch (error) {
      toast.error("Failed to reject estimate");
    }
  };

  const convertToJob = async (estimateId) => {
    try {
      const response = await fetch(`${API}/estimates/${estimateId}/convert-to-job`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        toast.success("Estimate converted to job");
        fetchEstimates();
        setIsDetailModalOpen(false);
        // Navigate to the new job if job_id is returned
        if (data.job_id) {
          window.location.href = `/jobs/${data.job_id}`;
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Failed to convert estimate to job");
      }
    } catch (error) {
      console.error("Convert to job error:", error);
      toast.error("Failed to convert estimate to job");
    }
  };

  const convertToInvoice = async (estimateId) => {
    try {
      const response = await fetch(`${API}/estimates/${estimateId}/convert-to-invoice`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Estimate converted to invoice");
        fetchEstimates();
        setIsDetailModalOpen(false);
      }
    } catch (error) {
      toast.error("Failed to convert estimate");
    }
  };

  const sendEstimate = async (estimate) => {
    // Validate customer has email
    if (!estimate.customer?.email) {
      toast.error("Customer does not have an email address");
      return;
    }
    
    try {
      const response = await fetch(`${API}/estimates/${estimate.estimate_id}/send`, {
        method: "POST",
        credentials: "include",
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Estimate sent to ${estimate.customer.email}`);
        fetchEstimates();
        // Update selected estimate if viewing detail modal
        if (selectedEstimate?.estimate_id === estimate.estimate_id) {
          setSelectedEstimate({ ...selectedEstimate, status: data.status || "sent" });
        }
        
        // Show share prompt (throttled to once per day)
        // Close detail modal first for better UX
        const shouldShow = shouldShowSharePrompt("estimate_sent");
        if (shouldShow) {
          setIsDetailModalOpen(false);
          // Small delay to let modal close animation complete
          setTimeout(() => setShowSharePrompt(true), 300);
        }
      } else {
        toast.error(data.detail || "Failed to send estimate");
      }
    } catch (error) {
      console.error("Error sending estimate:", error);
      toast.error("Failed to send estimate");
    }
  };

  // ========================
  // EDIT ESTIMATE
  // ========================

  const openEditModal = () => {
    if (!selectedEstimate) return;
    
    // Determine deposit config: use estimate's values or fall back to tenant defaults
    const hasDepositConfig = selectedEstimate.deposit_enabled !== undefined;
    const depositEnabled = hasDepositConfig 
      ? selectedEstimate.deposit_enabled 
      : depositDefaults.default_deposit_enabled;
    const depositType = hasDepositConfig && selectedEstimate.deposit_type
      ? selectedEstimate.deposit_type
      : depositDefaults.default_deposit_type;
    const depositValue = hasDepositConfig && selectedEstimate.deposit_value != null
      ? selectedEstimate.deposit_value
      : depositDefaults.default_deposit_value;
    
    setEditFormData({
      estimate_id: selectedEstimate.estimate_id,
      customer_id: selectedEstimate.customer_id,
      customer_name: selectedEstimate.customer?.name || "",
      title: selectedEstimate.title,
      items: selectedEstimate.items.map(item => ({
        description: item.description,
        quantity: item.quantity != null ? String(item.quantity) : "",
        price: item.price?.toFixed(2) || "",
        is_taxable: item.is_taxable || false,
      })),
      notes: selectedEstimate.notes || "",
      // Deposit config
      deposit_enabled: depositEnabled,
      deposit_type: depositType,
      deposit_value: depositValue,
    });
    setIsEditModalOpen(true);
  };

  const updateEditLineItem = (index, field, value) => {
    const newItems = [...editFormData.items];
    if (field === "quantity") {
      // Store as string to allow natural input editing (backspace, delete, cursor)
      newItems[index][field] = value;
    } else if (field === "is_taxable") {
      newItems[index][field] = value;
    } else {
      newItems[index][field] = value;
    }
    setEditFormData({ ...editFormData, items: newItems });
  };

  const formatEditPrice = (index) => {
    const newItems = [...editFormData.items];
    const val = parseFloat(newItems[index].price);
    if (!isNaN(val) && val > 0) {
      newItems[index].price = val.toFixed(2);
      // AUTO-FILL: If price is set and qty is empty, set qty to 1
      if (newItems[index].quantity === "" || newItems[index].quantity === null || newItems[index].quantity === undefined) {
        newItems[index].quantity = "1";
      }
    } else if (newItems[index].price === "" || newItems[index].price === "0") {
      newItems[index].price = "";
    }
    setEditFormData({ ...editFormData, items: newItems });
  };

  const addEditLineItem = () => {
    setEditFormData({
      ...editFormData,
      items: [...editFormData.items, { description: "", quantity: "", price: "", is_taxable: false }],
    });
  };

  const removeEditLineItem = (index) => {
    if (editFormData.items.length > 1) {
      setEditFormData({ ...editFormData, items: editFormData.items.filter((_, i) => i !== index) });
    }
  };

  const handleEditCatalogSelect = (index, catalogItem) => {
    const newItems = [...editFormData.items];
    const currentQuantity = newItems[index].quantity;
    // Preserve existing quantity if user has set one, otherwise keep empty
    newItems[index] = {
      ...newItems[index],
      description: catalogItem.description,
      price: catalogItem.price.toFixed(2),
      is_taxable: catalogItem.is_taxable,
      quantity: currentQuantity !== "" ? currentQuantity : "",
    };
    setEditFormData({ ...editFormData, items: newItems });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    // Import normalization function and normalize items before submit
    const { normalizeLineItems } = await import("../components/LineItemsEditor");
    const normalizedItems = normalizeLineItems(editFormData.items);
    
    try {
      const response = await fetch(`${API}/estimates/${editFormData.estimate_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: editFormData.title,
          items: normalizedItems,
          notes: editFormData.notes,
          // Include deposit config
          deposit_enabled: editFormData.deposit_enabled,
          deposit_type: editFormData.deposit_type,
          deposit_value: editFormData.deposit_value,
        }),
      });

      if (response.ok) {
        const updatedEstimate = await response.json();
        toast.success("Estimate updated");
        setIsEditModalOpen(false);
        setSelectedEstimate(updatedEstimate);
        fetchEstimates();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update estimate");
      }
    } catch (error) {
      toast.error("Failed to update estimate");
    }
  };

  // ========================
  // DELETE ESTIMATE
  // ========================

  const openDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteEstimate = async () => {
    if (!selectedEstimate) return;
    try {
      const response = await fetch(`${API}/estimates/${selectedEstimate.estimate_id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Estimate deleted");
        setIsDeleteDialogOpen(false);
        setIsDetailModalOpen(false);
        fetchEstimates();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete estimate");
      }
    } catch (error) {
      toast.error("Failed to delete estimate");
    }
  };

  // ========================
  // RENDER
  // ========================

  return (
    <div className="space-y-6" data-testid="estimates-page">
      {/* Controls Row - Status dropdown only (filter is in layout header) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-10 border-2" data-testid="status-filter-select">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Estimates</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          {/* Create Modal */}
          <EstimateCreateModal
            isOpen={isCreateModalOpen}
            onOpenChange={setIsCreateModalOpen}
            formData={formData}
            setFormData={setFormData}
            customers={customers}
            taxRatePercent={taxRatePercent}
            onSubmit={handleCreateSubmit}
            onUpdateLineItem={updateLineItem}
            onAddLineItem={addLineItem}
            onRemoveLineItem={removeLineItem}
            onCatalogSelect={handleCatalogSelect}
            onFormatPrice={formatPrice}
            onReset={resetForm}
            onCustomerCreated={(customer) => {
              setCustomers((prev) => [customer, ...prev]);
            }}
            validationErrors={lineItemErrors}
          />
        </div>
      </div>

      {/* Estimates List */}
      {loading ? (
        <div className="text-center py-12 text-zinc-500">Loading estimates...</div>
      ) : displayedEstimates.length === 0 && estimates.length === 0 ? (
        <Card className="border-2 border-dashed">
          <CardContent className="py-12 text-center">
            <FileText size={48} className="mx-auto mb-4 text-zinc-300" />
            <h3 className="font-heading text-lg uppercase mb-2">No estimates yet</h3>
            <p className="text-zinc-500 mb-4">Create your first estimate to get started</p>
          </CardContent>
        </Card>
      ) : displayedEstimates.length === 0 && (filterQuery.trim() || statusFilter !== "all") ? (
        <Card className="border-2 border-dashed border-zinc-300">
          <CardContent className="py-12 text-center">
            <FileText size={48} className="mx-auto mb-4 text-zinc-300" />
            <h3 className="font-heading text-lg uppercase mb-2">No Matching Estimates</h3>
            <p className="text-zinc-500">
              No estimates match your filter
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {/* Show filter indicator when filtering */}
          {filterQuery.trim() && (
            <div className="text-sm text-zinc-500 bg-zinc-100 px-3 py-2 rounded-lg" data-testid="filter-results-count">
              Showing {displayedEstimates.length} of {statusFilteredEstimates.length} estimates
            </div>
          )}
          {displayedEstimates.map((estimate) => (
            <Card
              key={estimate.estimate_id}
              className="border-2 hover:border-[#FF6600]/50 transition-colors cursor-pointer"
              onClick={() => viewEstimate(estimate)}
              data-testid={`estimate-card-${estimate.estimate_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-sm text-zinc-500 font-mono">{estimate.estimate_number}</span>
                      <Badge variant="outline" className={getStatusColor(estimate.status)}>
                        {estimate.status}
                      </Badge>
                      {/* P0 FIX: Show deposit paid badge if applicable */}
                      {(estimate.deposit_paid === true || estimate.deposit_status === "paid" || estimate.deposit_status === "PAID") && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="deposit-paid-badge">
                          Deposit paid
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-medium">{estimate.title}</h3>
                    <p className="text-sm text-zinc-500">{estimate.customer?.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-xl font-bold">{formatCurrency(estimate.total)}</p>
                    <p className="text-xs text-zinc-400">
                      {new Date(estimate.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="ml-4">
                        <DotsThree size={20} weight="bold" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); viewEstimate(estimate); }}>
                        <Eye size={16} className="mr-2" /> View Details
                      </DropdownMenuItem>
                      {canApprove(estimate.status) && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); approveEstimate(estimate.estimate_id); }}>
                          <CheckCircle size={16} className="mr-2" /> Approve
                        </DropdownMenuItem>
                      )}
                      {canReject(estimate.status) && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); rejectEstimate(estimate.estimate_id); }}>
                          <X size={16} className="mr-2" /> Mark as Rejected
                        </DropdownMenuItem>
                      )}
                      {canSendToCustomer(estimate.status) && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); sendEstimate(estimate); }}>
                          <PaperPlaneTilt size={16} className="mr-2" /> 
                          {estimate.status === "sent" ? "Resend to Customer" : "Send to Customer"}
                        </DropdownMenuItem>
                      )}
                      {canConvertToJob(estimate) && (
                        <>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); convertToJob(estimate.estimate_id); }}>
                            <Briefcase size={16} className="mr-2" /> Convert to Job
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); convertToInvoice(estimate.estimate_id); }}>
                            <Receipt size={16} className="mr-2" /> Convert to Invoice
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <EstimateDetailModal
        isOpen={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
        estimate={selectedEstimate}
        onApprove={approveEstimate}
        onReject={rejectEstimate}
        onConvertToJob={convertToJob}
        onConvertToInvoice={convertToInvoice}
        onSend={sendEstimate}
        onEdit={openEditModal}
        onDelete={openDeleteDialog}
        onDepositMarkedReceived={() => {
          // Refresh the selected estimate and list
          fetchEstimates();
          if (selectedEstimate?.estimate_id) {
            // Re-fetch the selected estimate to update deposit status
            fetch(`${API}/estimates/${selectedEstimate.estimate_id}`, { credentials: "include" })
              .then(res => res.json())
              .then(data => setSelectedEstimate(data))
              .catch(console.error);
          }
        }}
        paymentsEnabled={paymentsEnabled}
        getStatusColor={getStatusColor}
      />

      {/* Edit Modal */}
      <EstimateEditModal
        isOpen={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        taxRatePercent={taxRatePercent}
        onSubmit={handleEditSubmit}
        onUpdateLineItem={updateEditLineItem}
        onAddLineItem={addEditLineItem}
        onRemoveLineItem={removeEditLineItem}
        onCatalogSelect={handleEditCatalogSelect}
        onFormatPrice={formatEditPrice}
      />

      {/* Delete Dialog */}
      <EstimateDeleteDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteEstimate}
      />

      {/* Pay Deposit Modal (triggered after approval for online payments) */}
      <PayDepositModal
        isOpen={showPayDepositModal}
        onOpenChange={setShowPayDepositModal}
        depositInvoice={pendingDepositInvoice}
        estimateNumber={selectedEstimate?.estimate_number}
        onSuccess={() => {
          setShowPayDepositModal(false);
          setPendingDepositInvoice(null);
          fetchEstimates();
          toast.success("Deposit payment complete!");
        }}
        onSkip={() => {
          setShowPayDepositModal(false);
          setPendingDepositInvoice(null);
          fetchEstimates();
        }}
      />

      {/* Share Prompt Card */}
      {showSharePrompt && (
        <SharePromptCard onClose={() => setShowSharePrompt(false)} />
      )}

      {/* Quick Actions */}
      <BottomQuickActions />
    </div>
  );
}
